import { diffLines } from "diff";
import type { FingerprintOutput } from "../types/voice";

interface VoiceDiffViewProps {
  oldSystemPrompt: string;
  newSystemPrompt: string;
  oldProfile: FingerprintOutput;
  newProfile: FingerprintOutput;
  onApply: () => void;
  onDiscard: () => void;
  isApplying: boolean;
}

export const VoiceDiffView = ({
  oldSystemPrompt,
  newSystemPrompt,
  oldProfile,
  newProfile,
  onApply,
  onDiscard,
  isApplying,
}: VoiceDiffViewProps) => {
  const changes = diffLines(oldSystemPrompt, newSystemPrompt);

  const leftLines = changes.filter((c) => !c.added);
  const rightLines = changes.filter((c) => !c.removed);

  const renderLine = (
    value: string,
    added: boolean | undefined,
    removed: boolean | undefined,
    key: string
  ) => {
    let className = "text-slate-600";
    if (added) className = "bg-green-100 text-green-900";
    if (removed) className = "bg-red-100 text-red-900";

    return (
      <span key={key} className={className}>
        {value}
      </span>
    );
  };

  // Derive version numbers from profiles' summary field (used only for header labels).
  // Since we don't have version here, we use generic labels with old/new distinction.
  const oldVersion = "atual";
  const newVersion = "candidato";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Left column — old (atual) */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-100 border-b px-3 py-2 text-xs font-medium text-slate-600">
            v{oldVersion}
          </div>
          <div className="p-3 overflow-auto max-h-96">
            <pre className="text-sm font-mono whitespace-pre-wrap break-words">
              {leftLines.map((c, i) =>
                renderLine(c.value, c.added, c.removed, `left-${i}`)
              )}
            </pre>
          </div>
        </div>

        {/* Right column — new (candidato) */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-100 border-b px-3 py-2 text-xs font-medium text-slate-600">
            v{newVersion}
          </div>
          <div className="p-3 overflow-auto max-h-96">
            <pre className="text-sm font-mono whitespace-pre-wrap break-words">
              {rightLines.map((c, i) =>
                renderLine(c.value, c.added, c.removed, `right-${i}`)
              )}
            </pre>
          </div>
        </div>
      </div>

      {/* Profile summary diff hint */}
      {oldProfile.summary !== newProfile.summary && (
        <div className="text-xs text-slate-500 border rounded p-2 bg-slate-50">
          <span className="font-medium">Resumo alterado:</span> {newProfile.summary}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onDiscard}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
        >
          Descartar
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={isApplying}
          className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApplying ? "Aplicando…" : "Aplicar como v(N+1)"}
        </button>
      </div>
    </div>
  );
};
